import { ErrorHandler, Injectable, Injector } from '@angular/core';
import { ToasterService } from '@abp/ng.theme.shared';
import { HttpErrorResponse } from '@angular/common/http';

@Injectable()
export class CustomErrorHandler implements ErrorHandler {
  constructor(private injector: Injector) {}

  handleError(error: Error | HttpErrorResponse): void {
    const toaster = this.injector.get(ToasterService);

    if (error instanceof HttpErrorResponse) {
      // ABP error response format
      if (error.error?.error?.message) {
        // Translate ABP validation messages to Turkish
        const translatedMessage = this.translateAbpValidationMessage(error.error.error.message);
        toaster.error(translatedMessage);
      } else if (error.error?.error_description) {
        // OAuth error
        const turkishMessages: Record<string, string> = {
          'invalid_grant': 'Kullanıcı adı veya şifre hatalı!',
          'invalid_client': 'Geçersiz istemci bilgileri!',
          'invalid_request': 'Geçersiz istek!',
          'unauthorized_client': 'Yetkisiz istemci!',
          'unsupported_grant_type': 'Desteklenmeyen yetki türü!',
          'invalid_scope': 'Geçersiz kapsam!'
        };
        
        const message = turkishMessages[error.error.error] || error.error.error_description;
        toaster.error(message);
      } else {
        // Generic HTTP errors
        let message = 'Beklenmeyen bir hata oluştu.';
        
        switch (error.status) {
          case 0:
            message = 'Sunucuya bağlanılamıyor. İnternet bağlantınızı kontrol edin.';
            break;
          case 400:
            message = 'Geçersiz istek. Girdiğiniz bilgileri kontrol edin.';
            break;
          case 401:
            message = 'Oturumunuzun süresi doldu. Lütfen tekrar giriş yapın.';
            break;
          case 403:
            message = 'Bu işlemi gerçekleştirmek için yetkiniz yok.';
            break;
          case 404:
            message = 'İstenen kaynak bulunamadı.';
            break;
          case 500:
            message = 'Sunucu hatası. Lütfen daha sonra tekrar deneyin.';
            break;
          case 503:
            message = 'Hizmet şu anda kullanılamıyor. Lütfen daha sonra tekrar deneyin.';
            break;
        }
        
        toaster.error(message);
      }
    } else {
          }
  }

  private translateAbpValidationMessage(message: string): string {
    if (!message) return message;

    // ABP localization key format (handles both :: and : separators)
    if (message.includes('AbpValidation::') || message.includes('AbpValidation:')) {
      const keyMatch = message.match(/AbpValidation::?([A-Za-z]+)(?:\{(\d+)\})?(?:\{(\d+)\})?/);
      if (keyMatch) {
        const key = keyMatch[1];
        const param1 = keyMatch[2];
        const param2 = keyMatch[3];

        switch(key) {
          case 'ThisFieldMustBeAStringOrArrayTypeWithAMinimumLengthOf':
          case 'TheFieldMustBeAStringOrArrayTypeWithAMinimumLengthOf':
            return param1 ? `Bu alan en az ${param1} karakter olmalıdır.` : 'Bu alan minimum karakter sayısını karşılamalıdır.';
          case 'ThisFieldMustBeAStringOrArrayTypeWithAMaximumLengthOf':
          case 'TheFieldMustBeAStringOrArrayTypeWithAMaximumLengthOf':
            return param1 ? `Bu alan en fazla ${param1} karakter olabilir.` : 'Bu alan maksimum karakter sayısını aşmamalıdır.';
          case 'ThisFieldMustBeAStringWithAMinimumLengthOfAndAMaximumLengthOf':
          case 'TheFieldMustBeAStringWithAMinimumLengthOfAndAMaximumLengthOf':
            return (param1 && param2) ? `Bu alan en az ${param1} ve en fazla ${param2} karakter olmalıdır.` : 'Bu alan karakter limitlerine uymalıdır.';
          case 'ThisFieldIsRequired':
          case 'TheFieldIsRequired':
            return 'Bu alan zorunludur.';
          case 'ThisFieldIsNotAValidEmailAddress':
          case 'TheFieldIsNotAValidEmailAddress':
            return 'Geçerli bir e-posta adresi girin.';
          case 'ThisFieldIsNotAValidPhoneNumber':
          case 'TheFieldIsNotAValidPhoneNumber':
            return 'Geçerli bir telefon numarası girin.';
          case 'ThisFieldMustBeBetween':
          case 'TheFieldMustBeBetween':
            return (param1 && param2) ? `Bu alan ${param1} ile ${param2} arasında olmalıdır.` : 'Bu alan belirtilen aralıkta olmalıdır.';
          case 'ThisFieldIsNotValid':
          case 'TheFieldIsNotValid':
            return 'Bu alan geçerli değil.';
          default:
            return 'Bu alan geçersiz.';
        }
      }
    }

    // MinLength validation
    if (message.includes('ThisFieldMustBeAStringOrArrayTypeWithAMinimumLengthOf') ||
        message.includes('field must be a string or array type with a minimum length of')) {
      const match = message.match(/\{?(\d+)\}?/);
      if (match) {
        return `Bu alan en az ${match[1]} karakter olmalıdır.`;
      }
    }

    // MaxLength validation
    if (message.includes('ThisFieldMustBeAStringOrArrayTypeWithAMaximumLengthOf') ||
        message.includes('field must be a string or array type with a maximum length of')) {
      const match = message.match(/\{?(\d+)\}?/);
      if (match) {
        return `Bu alan en fazla ${match[1]} karakter olabilir.`;
      }
    }

    // Required field
    if (message.includes('ThisFieldIsRequired') ||
        message.includes('field is required')) {
      return 'Bu alan zorunludur.';
    }

    // Email validation
    if (message.includes('ThisFieldIsNotAValidEmailAddress') ||
        message.includes('is not a valid e-mail address')) {
      return 'Geçerli bir e-posta adresi girin.';
    }

    return message;
  }
}